/**
 * Audit Log Filters Component
 * Inline filter bar for audit logs - matches BulkEditTitles FilterBar pattern
 * Shared between Admin and Provider portals
 */

import { Filter, X } from "lucide-react";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../ui/select";

export interface AuditLogFilterValues {
  action: string;
  entityType: string;
  userId: string;
}

interface AuditLogFiltersProps {
  readonly filters: AuditLogFilterValues;
  readonly onFilterChange: (key: string, value: string) => void;
  readonly onClear: () => void;
  /** Hide user ID filter for provider view (they only see their own logs) */
  readonly hideUserFilter?: boolean;
  /** Limit action options to provider-relevant actions */
  readonly providerMode?: boolean;
}

/**
 * Map action codes to human-readable labels
 */
function getActionLabel(action: string): string {
  const labels: Record<string, string> = {
    // Provider-specific actions
    "submission.create": "Submission Created",
    "submission.update": "Submission Updated",
    "submission.submit": "Submission Submitted",
    "submission.withdraw": "Submission Withdrawn",
    "submission.resubmit": "Submission Resubmitted",
    "provider.profile.update": "Profile Updated",
    // Admin actions
    "submission.approve": "Submission Approved",
    "submission.reject": "Submission Rejected",
    TITLES_BULK_DELETED: "Titles Bulk Deleted",
    TITLES_BULK_ADDED_TO_COLLECTION: "Added to Collection",
    TITLES_BULK_REMOVED_FROM_COLLECTION: "Removed from Collection",
    COLLECTION_CREATED: "Collection Created",
    COLLECTION_UPDATED: "Collection Updated",
    COLLECTION_DELETED: "Collection Deleted",
    COLLECTION_ITEM_ADDED: "Item Added to Collection",
    COLLECTION_ITEM_REMOVED: "Item Removed from Collection",
    COLLECTION_ITEMS_REORDERED: "Items Reordered",
    TITLE_UPDATED: "Title Updated",
    IMAGES_TOGGLED_TO_TMDB: "Images → TMDB",
    IMAGES_TOGGLED_TO_R2: "Images → R2",
    IMAGES_CONFIRMED_AS_R2: "Images Confirmed R2",
  };
  return labels[action] || action;
}

/** Provider-specific action options */
const PROVIDER_ACTION_OPTIONS = [
  { value: "submission.create", label: "Submission Created" },
  { value: "submission.update", label: "Submission Updated" },
  { value: "submission.submit", label: "Submission Submitted" },
  { value: "submission.withdraw", label: "Submission Withdrawn" },
  { value: "submission.resubmit", label: "Submission Resubmitted" },
  { value: "provider.profile.update", label: "Profile Updated" },
];

/** Admin action options (full set) */
const ADMIN_ACTION_OPTIONS = [
  { value: "TITLE_UPDATED", label: "Title Updated" },
  { value: "TITLES_BULK_DELETED", label: "Titles Bulk Deleted" },
  { value: "TITLES_BULK_ADDED_TO_COLLECTION", label: "Added to Collection" },
  { value: "TITLES_BULK_REMOVED_FROM_COLLECTION", label: "Removed from Collection" },
  { value: "COLLECTION_CREATED", label: "Collection Created" },
  { value: "COLLECTION_UPDATED", label: "Collection Updated" },
  { value: "COLLECTION_ITEM_ADDED", label: "Item Added to Collection" },
  { value: "COLLECTION_ITEM_REMOVED", label: "Item Removed from Collection" },
  { value: "COLLECTION_ITEMS_REORDERED", label: "Items Reordered" },
  { value: "IMAGES_TOGGLED_TO_TMDB", label: "Images → TMDB" },
  { value: "IMAGES_TOGGLED_TO_R2", label: "Images → R2" },
  { value: "IMAGES_CONFIRMED_AS_R2", label: "Images Confirmed R2" },
  { value: "submission.approve", label: "Submission Approved" },
  { value: "submission.reject", label: "Submission Rejected" },
];

// eslint-disable-next-line max-lines-per-function
export function AuditLogFilters({
  filters,
  onFilterChange,
  onClear,
  hideUserFilter = false,
  providerMode = false,
}: AuditLogFiltersProps) {
  const hasActiveFilters = filters.action || filters.entityType || (!hideUserFilter && filters.userId);
  const actionOptions = providerMode ? PROVIDER_ACTION_OPTIONS : ADMIN_ACTION_OPTIONS;

  return (
    <div className="flex items-center gap-6 p-3 bg-neutral-900/30 border border-neutral-800 rounded-lg">
      <div className="flex items-center gap-2 text-sm font-medium text-white shrink-0">
        <Filter className={hasActiveFilters ? "h-4 w-4 text-lumi-accent" : "h-4 w-4"} />
        <span className={hasActiveFilters ? "text-lumi-accent" : ""}>Filters:</span>
      </div>

      <div className="flex flex-1 items-center gap-6">
        {/* Action Filter */}
        <div className="flex items-center gap-2">
          <span className={filters.action ? "text-xs text-lumi-accent shrink-0" : "text-xs text-neutral-400 shrink-0"}>
            Action:
          </span>
          <Select
            value={filters.action || "all"}
            onValueChange={(value: string) => onFilterChange("action", value === "all" ? "" : value)}
          >
            <SelectTrigger className="w-[200px] h-8">
              <span className={filters.action ? "text-sm text-lumi-accent" : "text-sm"}>
                {filters.action ? getActionLabel(filters.action) : "All"}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {actionOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Entity Type Filter */}
        <div className="flex items-center gap-2">
          <span
            className={filters.entityType ? "text-xs text-lumi-accent shrink-0" : "text-xs text-neutral-400 shrink-0"}
          >
            Entity:
          </span>
          <Select
            value={filters.entityType || "all"}
            onValueChange={(value: string) => onFilterChange("entityType", value === "all" ? "" : value)}
          >
            <SelectTrigger className="w-[140px] h-8">
              <span className={filters.entityType ? "text-sm text-lumi-accent" : "text-sm"}>
                {!filters.entityType && "All"}
                {filters.entityType === "submission" && "Submission"}
                {filters.entityType === "provider" && "Provider"}
                {filters.entityType === "title" && "Title"}
                {filters.entityType === "TITLE" && "Title"}
                {filters.entityType === "collection" && "Collection"}
                {filters.entityType === "collection_item" && "Collection Item"}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              <SelectItem value="submission">Submission</SelectItem>
              {providerMode && <SelectItem value="provider">Provider</SelectItem>}
              {!providerMode && (
                <>
                  <SelectItem value="title">Title (lowercase)</SelectItem>
                  <SelectItem value="TITLE">Title (uppercase)</SelectItem>
                  <SelectItem value="collection">Collection</SelectItem>
                  <SelectItem value="collection_item">Collection Item</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* User ID Filter - Admin only */}
        {!hideUserFilter && (
          <div className="flex items-center gap-2 min-w-[280px]">
            <span
              className={filters.userId ? "text-xs text-lumi-accent shrink-0" : "text-xs text-neutral-400 shrink-0"}
            >
              User ID:
            </span>
            <Input
              placeholder="Filter by user ID"
              value={filters.userId}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFilterChange("userId", e.target.value)}
              className="flex-1 h-8 bg-transparent"
            />
          </div>
        )}
      </div>

      {/* Clear All Button */}
      {hasActiveFilters && (
        <button
          type="button"
          onClick={onClear}
          className="flex items-center gap-1 px-3 py-1.5 text-xs text-neutral-400 hover:text-white hover:bg-neutral-800 rounded transition-colors shrink-0"
        >
          <X className="h-3 w-3" />
          <span>Clear</span>
        </button>
      )}
    </div>
  );
}
